import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Upload, FileCheck2, Link2, Loader2, UserPlus, BadgeCheck, FileWarning } from "lucide-react";
import { shortDateTime } from "@/lib/format";

type Doc = {
  id: string; owner_id: string; owner_role: string; doc_type: string;
  doc_number: string | null; storage_path: string; status: "submitted" | "verified" | "rejected";
  reviewer_note: string | null; submitted_at: string; reviewed_at: string | null;
};
type Profile = { id: string; full_name: string | null; phone: string | null; national_id: string | null; kyc_status: string };

const DOC_TYPES = [
  { v: "national_id",       l: "National ID (NIN)" },
  { v: "passport",          l: "Passport" },
  { v: "birth_certificate", l: "Child birth certificate" },
  { v: "school_letter",     l: "School admission letter" },
  { v: "guardian_consent",  l: "Guardian consent form" },
];

export function KycOnboarding({ role = "parent" }: { role?: "parent" | "student" | "merchant" }) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => (await supabase.from("profiles").select("id, full_name, phone, national_id, kyc_status").eq("id", user!.id).single()).data as Profile,
  });

  const { data: docs = [] } = useQuery({
    queryKey: ["kyc-docs", user?.id],
    queryFn: async () => ((await (supabase.from as any)("kyc_documents").select("*").eq("owner_id", user!.id).order("submitted_at", { ascending: false })).data ?? []) as Doc[],
  });

  const { data: links = [] } = useQuery({
    queryKey: ["wallet-links", user?.id],
    queryFn: async () => ((await supabase.from("wallet_links").select("*").eq("guardian_id", user!.id)).data ?? []),
  });

  const verifiedCount = docs.filter(d => d.status === "verified").length;
  const tierLabel = profile?.kyc_status === "verified" ? "Tier 2 · Verified" : verifiedCount > 0 ? "Tier 1 · Partial" : "Tier 0 · Unverified";
  const tierTone  = profile?.kyc_status === "verified" ? "success" : verifiedCount > 0 ? "ochre" : "muted";

  return (
    <div className="space-y-6">
      {/* Status hero */}
      <Card className="overflow-hidden shadow-card">
        <div className="grid gap-0 sm:grid-cols-[1fr_auto]">
          <div className="p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Identity verification</div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h2 className="font-display text-3xl">{profile?.full_name ?? "Welcome"}</h2>
              <Badge
                className="rounded-full px-3 py-1 text-[11px]"
                style={{
                  background: tierTone === "success" ? "color-mix(in oklab, var(--success) 18%, transparent)"
                            : tierTone === "ochre"   ? "color-mix(in oklab, var(--ochre) 22%, transparent)"
                            : "var(--muted)",
                  color: tierTone === "success" ? "var(--success)" : tierTone === "ochre" ? "var(--ochre)" : "var(--muted-foreground)",
                  border: "1px solid color-mix(in oklab, currentColor 30%, transparent)",
                }}
              >
                {profile?.kyc_status === "verified" ? <BadgeCheck className="mr-1 inline h-3 w-3" /> : <ShieldAlert className="mr-1 inline h-3 w-3" />}
                {tierLabel}
              </Badge>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Uganda PSP rules (BoU PSA 2020) require KYC before holding e-money.
              <strong className="text-foreground"> Tier 0</strong> wallets are read-only,
              <strong className="text-foreground"> Tier 1</strong> can hold up to UGX 1,000,000,
              and <strong className="text-foreground"> Tier 2</strong> unlocks unlimited family balances and merchant settlements.
            </p>
            <div className="mt-5 grid max-w-xl gap-3 sm:grid-cols-3">
              <Tier n={0} active={tierTone === "muted"}   label="Unverified"        cap="—"           />
              <Tier n={1} active={tierTone === "ochre"}   label="Partial"           cap="UGX 1M cap"  />
              <Tier n={2} active={tierTone === "success"} label="Fully verified"    cap="No cap"      />
            </div>
          </div>
          <div className="grid place-items-center bg-[color-mix(in_oklab,var(--ivory)_70%,transparent)] p-6 sm:w-56">
            <div className="text-center">
              <ShieldCheck className="mx-auto h-10 w-10 text-[var(--forest)]" />
              <div className="mt-2 font-display text-3xl">{verifiedCount}/{Math.max(2, docs.length)}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">documents verified</div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <DocumentsCard role={role} docs={docs} onChange={() => qc.invalidateQueries({ queryKey: ["kyc-docs"] })} />
        {role === "parent" && <ChildLinkingCard links={links} onChange={() => qc.invalidateQueries({ queryKey: ["wallet-links"] })} />}
      </div>
    </div>
  );
}

function Tier({ n, active, label, cap }: { n: number; active: boolean; label: string; cap: string }) {
  return (
    <div className={`rounded-xl border p-3 ${active ? "border-[var(--forest)] bg-[color-mix(in_oklab,var(--forest)_8%,transparent)]" : "bg-card"}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Tier {n}</div>
      <div className="mt-1 text-sm font-semibold">{label}</div>
      <div className="text-xs text-muted-foreground">{cap}</div>
    </div>
  );
}

function DocumentsCard({ role, docs, onChange }: { role: string; docs: Doc[]; onChange: () => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState("national_id");
  const [docNumber, setDocNumber] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Pick a file");
      const ext = file.name.split(".").pop() ?? "bin";
      const path = `${user!.id}/${docType}-${Date.now()}.${ext}`;
      const { error: e1 } = await supabase.storage.from("kyc-docs").upload(path, file, { upsert: false });
      if (e1) throw e1;
      const { error: e2 } = await (supabase.from as any)("kyc_documents").insert({
        owner_id: user!.id, owner_role: role, doc_type: docType, doc_number: docNumber || null, storage_path: path, status: "submitted",
      });
      if (e2) throw e2;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "kyc.document_submitted", entity_type: "kyc_document", metadata: { doc_type: docType, path } });
    },
    onSuccess: () => { toast.success("Document submitted for review"); setOpen(false); setFile(null); setDocNumber(""); onChange(); },
    onError: (e: Error) => toast.error(e.message),
  });

  // Demo helper — instant "verify" of latest submitted doc + bump profile when 2+ verified
  const simulateReview = useMutation({
    mutationFn: async (d: Doc) => {
      const status: "verified" | "rejected" = Math.random() < 0.92 ? "verified" : "rejected";
      await (supabase.from as any)("kyc_documents").update({
        status, reviewed_at: new Date().toISOString(), reviewer_note: status === "verified" ? "Auto-verified · demo" : "Image unclear · please re-upload",
      }).eq("id", d.id);
      if (status === "verified") {
        const verifiedCount = docs.filter(x => x.status === "verified").length + 1;
        if (verifiedCount >= 2) await supabase.from("profiles").update({ kyc_status: "verified" }).eq("id", user!.id);
      }
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: `kyc.${status}`, entity_type: "kyc_document", entity_id: d.id, metadata: {} });
    },
    onSuccess: () => { toast.success("Verification updated"); onChange(); },
  });

  return (
    <Card className="p-5 shadow-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-xl">Documents</div>
          <div className="text-xs text-muted-foreground">Upload at least two of: NIN, passport, school admission letter, or guardian consent.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="bg-[var(--terracotta)] text-primary-foreground hover:opacity-90"><Upload className="mr-1 h-3 w-3" />Upload</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload identity document</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Document number</Label>
                <Input value={docNumber} onChange={e => setDocNumber(e.target.value)} placeholder="e.g. CM12345…" />
              </div>
              <div>
                <Label>File (JPG/PNG/PDF, ≤ 5 MB)</Label>
                <Input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files?.[0] ?? null)} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => upload.mutate()} disabled={!file || upload.isPending}>
                {upload.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Submit for review
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-4 space-y-2">
        {docs.length === 0 && (
          <div className="rounded-xl border-2 border-dashed p-6 text-center text-sm text-muted-foreground">
            <FileWarning className="mx-auto mb-2 h-6 w-6 opacity-60" />
            No documents yet. Upload your NIN to unlock Tier 1.
          </div>
        )}
        {docs.map(d => (
          <div key={d.id} className="flex items-center justify-between rounded-xl border bg-card p-3 text-sm">
            <div className="flex items-center gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted"><FileCheck2 className="h-4 w-4" /></div>
              <div>
                <div className="font-medium">{DOC_TYPES.find(t => t.v === d.doc_type)?.l ?? d.doc_type}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.doc_number ?? "no ref"} · {shortDateTime(d.submitted_at)}</div>
                {d.reviewer_note && <div className="text-[11px] text-muted-foreground">{d.reviewer_note}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={d.status === "verified" ? "default" : d.status === "rejected" ? "destructive" : "secondary"}
              >{d.status}</Badge>
              {d.status === "submitted" && (
                <Button size="sm" variant="outline" onClick={() => simulateReview.mutate(d)} disabled={simulateReview.isPending}>
                  Simulate review
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ChildLinkingCard({ links, onChange }: { links: any[]; onChange: () => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [childEmail, setChildEmail] = useState("");
  const [relationship, setRelationship] = useState("parent");
  const [dailyLimit, setDailyLimit] = useState("8000");

  const link = useMutation({
    mutationFn: async () => {
      // Find child by email → profile by joining auth via rpc not allowed; in demo we look up wallet by owner whose email matches via profiles
      // Demo: use the email as a "claim ticket" — child must give parent their wallet UUID.
      // To keep this simple, accept either an email (looked up via student profile name) OR a UUID pasted in.
      let childUserId: string | null = null;
      let childWalletId: string | null = null;

      if (childEmail.length === 36 && childEmail.includes("-")) {
        // pasted wallet UUID
        const { data: w } = await supabase.from("wallets").select("id, owner_id").eq("id", childEmail.trim()).maybeSingle();
        if (!w) throw new Error("No wallet with that ID");
        childWalletId = w.id; childUserId = w.owner_id;
      } else {
        // assume it's an email — search profiles by id from auth.users requires admin; demo path: look up by phone or name
        const { data: p } = await supabase.from("profiles").select("id").eq("phone", childEmail.trim()).maybeSingle();
        if (!p) throw new Error("Use the child's wallet UUID (paste it from their student app · QR tab).");
        const { data: w } = await supabase.from("wallets").select("id").eq("owner_id", p.id).eq("owner_type", "student").maybeSingle();
        if (!w) throw new Error("Child has no student wallet yet");
        childWalletId = w.id; childUserId = p.id;
      }

      const { error } = await supabase.from("wallet_links").insert({
        guardian_id: user!.id, child_user_id: childUserId!, child_wallet_id: childWalletId!,
        relationship, daily_limit_ugx: Number(dailyLimit) || null,
      });
      if (error) throw error;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "kyc.child_linked", entity_type: "wallet_link", metadata: { child_user_id: childUserId } });
    },
    onSuccess: () => { toast.success("Child linked"); setOpen(false); setChildEmail(""); onChange(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const unlink = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("wallet_links").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Unlinked"); onChange(); },
  });

  return (
    <Card className="p-5 shadow-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-xl">Linked children</div>
          <div className="text-xs text-muted-foreground">A guardian-child link unlocks approvals, allowance caps, and live ledger visibility.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline"><UserPlus className="mr-1 h-3 w-3" />Link child</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Link a child wallet</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">
                Ask your child to open <strong>Student app → QR & wallet</strong> and copy their wallet ID, then paste it here.
              </div>
              <div>
                <Label>Child wallet ID (UUID) or phone</Label>
                <Input value={childEmail} onChange={e => setChildEmail(e.target.value)} placeholder="e.g. 9c7e…-…-… or +2567…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Relationship</Label>
                  <Select value={relationship} onValueChange={setRelationship}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="guardian">Legal guardian</SelectItem>
                      <SelectItem value="sponsor">Sponsor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Daily cap (UGX)</Label>
                  <Input type="number" value={dailyLimit} onChange={e => setDailyLimit(e.target.value)} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => link.mutate()} disabled={!childEmail || link.isPending}>
                {link.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Link & save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-4 space-y-2">
        {links.length === 0 && (
          <div className="rounded-xl border-2 border-dashed p-6 text-center text-sm text-muted-foreground">
            <Link2 className="mx-auto mb-2 h-6 w-6 opacity-60" />
            No children linked yet. Demo children seeded automatically still appear in the Overview tab.
          </div>
        )}
        {links.map((l: any) => (
          <div key={l.id} className="flex items-center justify-between rounded-xl border bg-card p-3 text-sm">
            <div>
              <div className="font-medium">{l.relationship ?? "parent"} · wallet {l.child_wallet_id.slice(0, 8)}…</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">daily cap {l.daily_limit_ugx ? `UGX ${Number(l.daily_limit_ugx).toLocaleString("en-UG")}` : "none"}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => unlink.mutate(l.id)}>Unlink</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
